/**
 * Genesis M8 · Slice 7.2/7.3 · Ads Growth Loop console.
 *
 * A single, deliberately compact console that unifies the four new
 * capabilities behind a tabbed layout. Every action falls back to
 * sandbox envelopes when META_ADS_LIVE_ENABLED=0, so this page is safe
 * to open in preview environments.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const TAB_KEYS = ["insights", "audiences", "leads", "capi"];
const TAB_LABELS = {
  insights: "Insights",
  audiences: "Audiences",
  leads: "Lead Ads",
  capi: "Conversions API",
};

function StatCard({ label, value, hint, testid }) {
  return (
    <div
      className="bo-card"
      style={{ padding: 12, minWidth: 140 }}
      data-testid={testid}
    >
      <div className="bo-eyebrow" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontFamily: "var(--bo-serif)", fontSize: 22, marginTop: 2 }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>{hint}</div>
      ) : null}
    </div>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      className="bo-card"
      style={{
        marginTop: 8,
        padding: "8px 10px",
        borderLeft: "3px solid #8a2b0a",
        color: "#8a2b0a",
        background: "var(--bo-panel-alt, #f6f4ef)",
      }}
      data-testid="ads-growth-error"
    >
      {error}
    </div>
  );
}

function useAdAccounts() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/ads/ad-accounts");
      setItems(r.data.ad_accounts || r.data.items || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { adAccounts: items, adAccountsError: error, adAccountsLoading: loading, reloadAdAccounts: load };
}

// ---------------------------------------------------------------------------
// Insights tab
// ---------------------------------------------------------------------------

function TrendChart({ dates, spend, conversions }) {
  // Compact dual-axis line chart. Left axis = spend, right = conversions.
  const w = 720;
  const h = 160;
  const padL = 44;
  const padR = 44;
  const padT = 14;
  const padB = 24;
  const iw = w - padL - padR;
  const ih = h - padT - padB;
  if (!dates.length) {
    return (
      <div
        className="bo-card"
        style={{ padding: 12, marginBottom: 8, color: "var(--bo-muted)", fontSize: 12 }}
        data-testid="insights-trend-empty"
      >
        Pull insights to see a spend + conversions trend chart here.
      </div>
    );
  }
  const maxSpend = Math.max(1, ...spend);
  const maxConv = Math.max(1, ...conversions);
  const stepX = dates.length > 1 ? iw / (dates.length - 1) : iw / 2;
  const xAt = (i) => padL + i * stepX;
  const ySpend = (v) => padT + ih - (v / maxSpend) * ih;
  const yConv = (v) => padT + ih - (v / maxConv) * ih;
  const spendPath = spend
    .map((v, i) => (i === 0 ? "M" : "L") + xAt(i).toFixed(1) + " " + ySpend(v).toFixed(1))
    .join(" ");
  const convPath = conversions
    .map((v, i) => (i === 0 ? "M" : "L") + xAt(i).toFixed(1) + " " + yConv(v).toFixed(1))
    .join(" ");
  const spendColor = "#b8860b";
  const convColor = "#22c55e";
  const gridColor = "var(--bo-border, #e6e2d8)";
  return (
    <div
      className="bo-card"
      style={{ padding: 12, marginBottom: 8 }}
      data-testid="insights-trend-chart"
    >
      <div
        style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "baseline", marginBottom: 4, fontSize: 12,
        }}
      >
        <div className="bo-eyebrow" style={{ fontSize: 11 }}>Trend</div>
        <div style={{ display: "flex", gap: 12 }}>
          <span data-testid="insights-trend-legend-spend">
            <span style={{ display: "inline-block", width: 10, height: 2, background: spendColor, marginRight: 4, verticalAlign: "middle" }} />
            Spend
          </span>
          <span data-testid="insights-trend-legend-conv">
            <span style={{ display: "inline-block", width: 10, height: 2, background: convColor, marginRight: 4, verticalAlign: "middle" }} />
            Conversions
          </span>
        </div>
      </div>
      <svg
        width="100%" viewBox={`0 0 ${w} ${h}`}
        style={{ maxWidth: "100%", display: "block" }}
        role="img"
        aria-label="Spend and conversions over selected date range"
      >
        {/* baseline */}
        <line x1={padL} y1={padT + ih} x2={padL + iw} y2={padT + ih} stroke={gridColor} strokeWidth={1} />
        <line x1={padL} y1={padT} x2={padL} y2={padT + ih} stroke={gridColor} strokeWidth={1} />
        <line x1={padL + iw} y1={padT} x2={padL + iw} y2={padT + ih} stroke={gridColor} strokeWidth={1} />
        {/* left axis label */}
        <text x={padL - 4} y={padT + 10} textAnchor="end" fontSize="10" fill={spendColor}>${maxSpend.toFixed(0)}</text>
        <text x={padL - 4} y={padT + ih + 4} textAnchor="end" fontSize="10" fill={spendColor}>$0</text>
        {/* right axis */}
        <text x={padL + iw + 4} y={padT + 10} textAnchor="start" fontSize="10" fill={convColor}>{maxConv.toFixed(0)}</text>
        <text x={padL + iw + 4} y={padT + ih + 4} textAnchor="start" fontSize="10" fill={convColor}>0</text>
        {/* spend line */}
        <path d={spendPath} stroke={spendColor} strokeWidth={2} fill="none" />
        {/* conv line */}
        <path d={convPath} stroke={convColor} strokeWidth={2} fill="none" strokeDasharray="4 3" />
        {/* points */}
        {dates.map((_, i) => (
          <g key={i}>
            <circle cx={xAt(i)} cy={ySpend(spend[i])} r={2.5} fill={spendColor} />
            <circle cx={xAt(i)} cy={yConv(conversions[i])} r={2.5} fill={convColor} />
          </g>
        ))}
        {/* x-axis labels — first, middle, last */}
        {[0, Math.floor((dates.length - 1) / 2), dates.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i)
          .map((i) => (
            <text
              key={i} x={xAt(i)} y={h - 6}
              textAnchor={i === 0 ? "start" : i === dates.length - 1 ? "end" : "middle"}
              fontSize="10" fill="var(--bo-muted)"
            >
              {dates[i]}
            </text>
          ))}
      </svg>
    </div>
  );
}

function InsightsTab({ ad_accounts }) {
  const [ad_account_id, setAdAccountId] = useState("");
  const [level, setLevel] = useState("campaign");
  const [bucket, setBucket] = useState("daily");
  const [since, setSince] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [until, setUntil] = useState(() => new Date().toISOString().slice(0, 10));
  const [attribution, setAttribution] = useState(["7d_click", "1d_view"]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pullSummary, setPullSummary] = useState(null);

  useEffect(() => {
    if (!ad_account_id && ad_accounts.length) {
      setAdAccountId(ad_accounts[0].id);
    }
  }, [ad_accounts, ad_account_id]);

  useEffect(() => {
    if (!ad_account_id) return;
    (async () => {
      try {
        const r = await api.get(
          `/ads/ad-accounts/${ad_account_id}/attribution-window`
        );
        if (r.data?.windows?.length) setAttribution(r.data.windows);
      } catch (_) {
        /* keep default */
      }
    })();
  }, [ad_account_id]);

  const saveAttribution = async (windows) => {
    setAttribution(windows);
    if (!ad_account_id) return;
    try {
      await api.post(`/ads/ad-accounts/${ad_account_id}/attribution-window`, {
        windows,
      });
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const pull = async () => {
    if (!ad_account_id) return;
    setBusy(true);
    setError(null);
    setPullSummary(null);
    try {
      const r = await api.post("/ads/insights/pull", {
        ad_account_id, level, bucket, since, until,
        attribution_windows: attribution,
      });
      setPullSummary(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
      await refreshRows();
    }
  };

  const refreshRows = useCallback(async () => {
    if (!ad_account_id) return;
    try {
      const r = await api.get(`/ads/insights`, {
        params: { ad_account_id, level, bucket, since, until, limit: 200 },
      });
      setRows(r.data.rows || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [ad_account_id, level, bucket, since, until]);

  useEffect(() => {
    refreshRows();
  }, [refreshRows]);

  const totals = useMemo(() => {
    const t = { spend: 0, impressions: 0, clicks: 0, conversions: 0 };
    rows.forEach((r) => {
      t.spend += Number(r.spend) || 0;
      t.impressions += Number(r.impressions) || 0;
      t.clicks += Number(r.clicks) || 0;
      t.conversions += Number(r.conversions) || 0;
    });
    return t;
  }, [rows]);

  // ------------------------------------------------------------------
  // M9 · Insights Trend Chart — collapse rows to per-date buckets and
  // draw a compact dual-line SVG (spend + conversions) so operators see
  // trend at a glance without pulling a heavy charting library.
  // ------------------------------------------------------------------
  const trendSeries = useMemo(() => {
    if (!rows.length) return { dates: [], spend: [], conversions: [] };
    const map = new Map();
    for (const r of rows) {
      const d = r.date_start || "";
      if (!d) continue;
      const cur = map.get(d) || { spend: 0, conversions: 0 };
      cur.spend += Number(r.spend) || 0;
      cur.conversions += Number(r.conversions) || 0;
      map.set(d, cur);
    }
    const dates = [...map.keys()].sort();
    return {
      dates,
      spend: dates.map((d) => map.get(d).spend),
      conversions: dates.map((d) => map.get(d).conversions),
    };
  }, [rows]);

  return (
    <div data-testid="ads-insights-tab">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <label className="bo-field" style={{ minWidth: 220 }}>
          <span className="bo-eyebrow">Ad Account</span>
          <select
            className="bo-input"
            value={ad_account_id}
            onChange={(e) => setAdAccountId(e.target.value)}
            data-testid="insights-account-select"
          >
            {ad_accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {`${a.display_name} · ${a.external_ad_account_id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="bo-field">
          <span className="bo-eyebrow">Level</span>
          <select
            className="bo-input"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            data-testid="insights-level-select"
          >
            {["campaign", "adset", "ad", "creative"].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </label>
        <label className="bo-field">
          <span className="bo-eyebrow">Bucket</span>
          <select
            className="bo-input"
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            data-testid="insights-bucket-select"
          >
            {["hourly", "daily", "weekly"].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </label>
        <label className="bo-field">
          <span className="bo-eyebrow">Since</span>
          <input
            className="bo-input"
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            data-testid="insights-since"
          />
        </label>
        <label className="bo-field">
          <span className="bo-eyebrow">Until</span>
          <input
            className="bo-input"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            data-testid="insights-until"
          />
        </label>
        <button
          type="button"
          className="bo-btn bo-btn--primary"
          onClick={pull}
          disabled={busy || !ad_account_id}
          data-testid="insights-pull-btn"
          style={{ alignSelf: "flex-end" }}
        >
          {busy ? "Pulling…" : "Pull insights"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <StatCard label="Spend" value={`$${totals.spend.toFixed(2)}`} testid="insights-total-spend" />
        <StatCard label="Impressions" value={totals.impressions.toLocaleString()} testid="insights-total-imp" />
        <StatCard label="Clicks" value={totals.clicks.toLocaleString()} testid="insights-total-clicks" />
        <StatCard
          label="Conversions"
          value={totals.conversions}
          hint={totals.conversions ? `CPA $${(totals.spend / totals.conversions).toFixed(2)}` : "No conv."}
          testid="insights-total-conv"
        />
        <div
          className="bo-card"
          style={{ padding: 12, minWidth: 240 }}
          data-testid="insights-attribution-card"
        >
          <div className="bo-eyebrow" style={{ fontSize: 11 }}>Attribution window</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {["1d_click", "7d_click", "28d_click", "1d_view", "7d_view"].map((w) => {
              const on = attribution.includes(w);
              return (
                <button
                  key={w}
                  type="button"
                  className={`bo-btn ${on ? "bo-btn--primary" : "bo-btn--ghost"}`}
                  onClick={() => {
                    const next = on
                      ? attribution.filter((x) => x !== w)
                      : [...attribution, w];
                    if (next.length) saveAttribution(next);
                  }}
                  data-testid={`insights-attr-${w}`}
                  style={{ padding: "2px 8px", fontSize: 11 }}
                >
                  {w}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <ErrorBanner error={error} />

      {pullSummary ? (
        <div
          className="bo-card"
          style={{
            padding: 8, marginBottom: 8, fontSize: 12,
            borderLeft: "3px solid #1f6c3f",
          }}
          data-testid="insights-pull-summary"
        >
          Pulled {pullSummary.row_count} row{pullSummary.row_count === 1 ? "" : "s"}
          {" · "}job {pullSummary.job_id.slice(0, 8)}…
          {" · "}window {pullSummary.attribution_window}
        </div>
      ) : null}

      <TrendChart
        dates={trendSeries.dates}
        spend={trendSeries.spend}
        conversions={trendSeries.conversions}
      />

      <div style={{ overflowX: "auto" }}>
        <table
          className="bo-table"
          style={{ width: "100%", fontSize: 12 }}
          data-testid="insights-table"
        >
          <thead>
            <tr>
              <th>Date</th>
              <th>External id</th>
              <th style={{ textAlign: "right" }}>Spend</th>
              <th style={{ textAlign: "right" }}>Impr.</th>
              <th style={{ textAlign: "right" }}>Reach</th>
              <th style={{ textAlign: "right" }}>Clicks</th>
              <th style={{ textAlign: "right" }}>CTR</th>
              <th style={{ textAlign: "right" }}>CPC</th>
              <th style={{ textAlign: "right" }}>CPM</th>
              <th style={{ textAlign: "right" }}>Freq.</th>
              <th style={{ textAlign: "right" }}>Conv.</th>
              <th style={{ textAlign: "right" }}>CPA</th>
              <th style={{ textAlign: "right" }}>ROAS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={13} style={{ textAlign: "center", padding: 20, color: "var(--bo-muted)" }}>
                  No rows yet. Click "Pull insights" to fetch from Meta.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.external_id}-${r.date_start}-${i}`} data-testid={`insights-row-${i}`}>
                  <td>{r.date_start}</td>
                  <td style={{ fontFamily: "monospace" }}>{r.external_id}</td>
                  <td style={{ textAlign: "right" }}>${Number(r.spend).toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{r.impressions}</td>
                  <td style={{ textAlign: "right" }}>{r.reach}</td>
                  <td style={{ textAlign: "right" }}>{r.clicks}</td>
                  <td style={{ textAlign: "right" }}>{Number(r.ctr).toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>${Number(r.cpc).toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>${Number(r.cpm).toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{Number(r.frequency).toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{r.conversions}</td>
                  <td style={{ textAlign: "right" }}>{r.cpa != null ? `$${r.cpa}` : "—"}</td>
                  <td style={{ textAlign: "right" }}>{r.roas != null ? r.roas : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audiences tab
// ---------------------------------------------------------------------------

function AudiencesTab({ ad_accounts }) {
  const [ad_account_id, setAdAccountId] = useState("");
  const [name, setName] = useState("");
  const [seedPolicy, setSeedPolicy] = useState("static");
  const [audiences, setAudiences] = useState([]);
  const [busy, setBusy] = useState(false);
  const [presetBusy, setPresetBusy] = useState(null);
  const [error, setError] = useState(null);

  // Retargeting presets — dynamic queries that need zero owner input.
  const PRESETS = [
    {
      key: "waitlist_30d",
      label: "Waitlist · last 30 days",
      description: "Everyone who joined the waitlist in the last 30 days.",
      body: () => ({
        name: `Waitlist · last 30 days · ${new Date().toISOString().slice(0, 10)}`,
        seed_policy: "dynamic",
        schema_keys: ["EMAIL"],
        dynamic_query: {
          collection: "waitlist",
          filter: {
            unsubscribed_at: { $exists: false },
            created_at: { $gte_days_ago: 30 },
          },
          fields_map: { email: "email" },
        },
      }),
    },
    {
      key: "leads_30d",
      label: "Leads · last 30 days",
      description: "Every Meta Lead Ads submission in the last 30 days.",
      body: () => ({
        name: `Leads · last 30 days · ${new Date().toISOString().slice(0, 10)}`,
        seed_policy: "dynamic",
        schema_keys: ["EMAIL"],
        dynamic_query: {
          collection: "leads",
          filter: { received_at: { $gte_days_ago: 30 } },
          fields_map: { email: "email" },
        },
      }),
    },
    {
      key: "signed_in_users",
      label: "Signed-in users",
      description: "Every active user of your workspace (org-scoped).",
      body: () => ({
        name: `Signed-in users · ${new Date().toISOString().slice(0, 10)}`,
        seed_policy: "dynamic",
        schema_keys: ["EMAIL"],
        dynamic_query: {
          collection: "users",
          filter: { status: "active" },
          fields_map: { email: "email" },
        },
      }),
    },
  ];

  useEffect(() => {
    if (!ad_account_id && ad_accounts.length) setAdAccountId(ad_accounts[0].id);
  }, [ad_accounts, ad_account_id]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ads/audiences`, {
        params: ad_account_id ? { ad_account_id } : undefined,
      });
      setAudiences(r.data.audiences || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [ad_account_id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/ads/audiences`, {
        ad_account_id, name,
        seed_policy: seedPolicy,
        schema_keys: ["EMAIL"],
        dynamic_query:
          seedPolicy === "dynamic"
            ? { collection: "users", filter: {}, fields_map: { email: "email" } }
            : null,
      });
      setName("");
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runPreset = async (preset) => {
    if (!ad_account_id) {
      setError("Pick an ad account first.");
      return;
    }
    setPresetBusy(preset.key);
    setError(null);
    try {
      const shape = preset.body();
      const created = await api.post(`/ads/audiences`, {
        ad_account_id, ...shape,
      });
      // Immediately sync so the audience isn't left empty.
      try {
        await api.post(`/ads/audiences/${created.data.id}/sync`, {});
      } catch (syncErr) {
        // Sync failure (e.g. dynamic_query_empty) is non-fatal — the
        // audience exists and can be re-synced later once its source
        // has rows.
        // eslint-disable-next-line no-console
        console.warn("preset sync soft-fail", syncErr);
      }
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setPresetBusy(null);
    }
  };

  const syncStatic = async (aud) => {
    const raw = window.prompt("Paste one email per line:");
    if (!raw) return;
    const users = raw
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((email) => ({ email }));
    try {
      await api.post(`/ads/audiences/${aud.id}/sync`, { users });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const syncDynamic = async (aud) => {
    try {
      await api.post(`/ads/audiences/${aud.id}/sync`, {});
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const refreshStatus = async (aud) => {
    try {
      await api.get(`/ads/audiences/${aud.id}/status`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div data-testid="ads-audiences-tab">
      <div
        className="bo-card"
        style={{ padding: 12, marginBottom: 12 }}
        data-testid="audiences-presets"
      >
        <div
          style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "baseline", marginBottom: 6, gap: 12, flexWrap: "wrap",
          }}
        >
          <div>
            <div className="bo-eyebrow" style={{ fontSize: 11 }}>Retargeting presets</div>
            <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>
              One-click dynamic audiences — creates + auto-syncs the audience
              against the picked ad account.
            </div>
          </div>
          <select
            className="bo-input"
            value={ad_account_id}
            onChange={(e) => setAdAccountId(e.target.value)}
            data-testid="audiences-preset-account-select"
            style={{ minWidth: 220 }}
          >
            {ad_accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.display_name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="bo-btn bo-btn--secondary"
              onClick={() => runPreset(p)}
              disabled={!!presetBusy || !ad_account_id}
              data-testid={`audiences-preset-${p.key}`}
              title={p.description}
              style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", padding: 10, minWidth: 220 }}
            >
              <span style={{ fontWeight: 600 }}>
                {presetBusy === p.key ? "Creating…" : p.label}
              </span>
              <span
                style={{
                  fontSize: 11, color: "var(--bo-muted)",
                  fontWeight: 400, marginTop: 2, textAlign: "left",
                }}
              >
                {p.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="bo-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="bo-field" style={{ minWidth: 220 }}>
            <span className="bo-eyebrow">Ad Account</span>
            <select
              className="bo-input"
              value={ad_account_id}
              onChange={(e) => setAdAccountId(e.target.value)}
              data-testid="audiences-account-select"
            >
              {ad_accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.display_name}</option>
              ))}
            </select>
          </label>
          <label className="bo-field" style={{ minWidth: 220, flex: 1 }}>
            <span className="bo-eyebrow">Audience name</span>
            <input
              className="bo-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Waitlist — Aug 2026"
              data-testid="audiences-name-input"
            />
          </label>
          <label className="bo-field">
            <span className="bo-eyebrow">Seed policy</span>
            <select
              className="bo-input"
              value={seedPolicy}
              onChange={(e) => setSeedPolicy(e.target.value)}
              data-testid="audiences-seed-select"
            >
              <option value="static">Static list</option>
              <option value="dynamic">Dynamic (users query)</option>
            </select>
          </label>
          <button
            type="button"
            className="bo-btn bo-btn--primary"
            onClick={create}
            disabled={busy || !ad_account_id || !name}
            data-testid="audiences-create-btn"
          >
            Create
          </button>
        </div>
      </div>

      <ErrorBanner error={error} />

      <table className="bo-table" style={{ width: "100%", fontSize: 12 }}
             data-testid="audiences-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Policy</th>
            <th style={{ textAlign: "right" }}>Uploaded</th>
            <th style={{ textAlign: "right" }}>Approx. count</th>
            <th style={{ textAlign: "right" }}>Match rate</th>
            <th>Op. status</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {audiences.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ padding: 18, textAlign: "center", color: "var(--bo-muted)" }}>
                No audiences yet. Create one above.
              </td>
            </tr>
          ) : (
            audiences.map((a) => (
              <tr key={a.id} data-testid={`audiences-row-${a.id}`}>
                <td>
                  <div style={{ fontWeight: 600 }}>{a.name}</div>
                  <div style={{ fontSize: 10, color: "var(--bo-muted)" }}>{a.external_audience_id}</div>
                </td>
                <td>{a.seed_policy}</td>
                <td style={{ textAlign: "right" }}>{a.last_uploaded_count || 0}</td>
                <td style={{ textAlign: "right" }}>
                  {a.match_status?.approximate_count ?? "—"}
                </td>
                <td style={{ textAlign: "right" }}>
                  {a.match_status?.approximate_match_rate != null
                    ? `${(a.match_status.approximate_match_rate * 100).toFixed(1)}%`
                    : "—"}
                </td>
                <td style={{ fontSize: 11 }}>
                  {a.match_status?.operation_status?.description || "—"}
                </td>
                <td style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="bo-btn bo-btn--ghost"
                    onClick={() => (a.seed_policy === "dynamic" ? syncDynamic(a) : syncStatic(a))}
                    data-testid={`audiences-sync-btn-${a.id}`}
                    style={{ marginRight: 4 }}
                  >
                    Sync
                  </button>
                  <button
                    type="button"
                    className="bo-btn bo-btn--ghost"
                    onClick={() => refreshStatus(a)}
                    data-testid={`audiences-refresh-status-${a.id}`}
                  >
                    Refresh status
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leads tab
// ---------------------------------------------------------------------------

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ads/leads?limit=100`);
      setLeads(r.data.leads || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const promote = async (id) => {
    setBusy(id);
    setError(null);
    try {
      await api.post(`/ads/leads/${id}/promote`, { fetch_fields: true });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="ads-leads-tab">
      <ErrorBanner error={error} />
      <table className="bo-table" style={{ width: "100%", fontSize: 12 }}
             data-testid="leads-table">
        <thead>
          <tr>
            <th>Leadgen ID</th>
            <th>Form</th>
            <th>Ad</th>
            <th>Fields</th>
            <th>Fetched</th>
            <th>Promoted</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ padding: 18, textAlign: "center", color: "var(--bo-muted)" }}>
                No leads yet. When your Meta Lead Ads form is live, leads land here automatically.
              </td>
            </tr>
          ) : (
            leads.map((l) => (
              <tr key={l.id} data-testid={`leads-row-${l.id}`}>
                <td style={{ fontFamily: "monospace" }}>{l.leadgen_id}</td>
                <td style={{ fontFamily: "monospace" }}>{l.form_id || "—"}</td>
                <td style={{ fontFamily: "monospace" }}>{l.ad_id || "—"}</td>
                <td>
                  {(l.field_data || []).length > 0
                    ? l.field_data.map((f) => f.name).join(", ")
                    : "—"}
                </td>
                <td>{l.fetch_status}</td>
                <td>{l.promoted_status}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="bo-btn bo-btn--primary"
                    onClick={() => promote(l.id)}
                    disabled={busy === l.id}
                    data-testid={`leads-promote-btn-${l.id}`}
                  >
                    {busy === l.id ? "Promoting…" : "Promote to Genesis"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CAPI tab
// ---------------------------------------------------------------------------

function CapiTab({ ad_accounts }) {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [ad_account_id, setAdAccountId] = useState("");
  const [pixelId, setPixelId] = useState("");
  const [eventName, setEventName] = useState("Lead");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ad_account_id && ad_accounts.length) setAdAccountId(ad_accounts[0].id);
  }, [ad_accounts, ad_account_id]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ads/capi/events?limit=50`);
      setEvents(r.data.events || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/ads/capi/events`, {
        ad_account_id, pixel_id: pixelId,
        events: [{ event_name: eventName, user: { email } }],
      });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ads-capi-tab">
      <div className="bo-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="bo-field">
            <span className="bo-eyebrow">Ad Account</span>
            <select
              className="bo-input"
              value={ad_account_id}
              onChange={(e) => setAdAccountId(e.target.value)}
              data-testid="capi-account-select"
            >
              {ad_accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.display_name}</option>
              ))}
            </select>
          </label>
          <label className="bo-field">
            <span className="bo-eyebrow">Pixel ID</span>
            <input
              className="bo-input"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder="test-pixel-1"
              data-testid="capi-pixel-input"
            />
          </label>
          <label className="bo-field">
            <span className="bo-eyebrow">Event</span>
            <select
              className="bo-input"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              data-testid="capi-event-select"
            >
              {["PageView", "Lead", "Contact", "Subscribe", "Purchase"].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <label className="bo-field" style={{ minWidth: 200, flex: 1 }}>
            <span className="bo-eyebrow">User email (hashed before send)</span>
            <input
              className="bo-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="lead@example.com"
              data-testid="capi-email-input"
            />
          </label>
          <button
            type="button"
            className="bo-btn bo-btn--primary"
            onClick={send}
            disabled={busy || !ad_account_id || !pixelId}
            data-testid="capi-send-btn"
          >
            {busy ? "Sending…" : "Send event"}
          </button>
        </div>
      </div>

      <ErrorBanner error={error} />

      <table className="bo-table" style={{ width: "100%", fontSize: 12 }}
             data-testid="capi-events-table">
        <thead>
          <tr>
            <th>Sent at</th>
            <th>Pixel</th>
            <th>Events</th>
            <th>Live</th>
            <th>Status</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ padding: 18, textAlign: "center", color: "var(--bo-muted)" }}>
                No deliveries yet.
              </td>
            </tr>
          ) : (
            events.map((e, i) => (
              <tr key={e.id || i} data-testid={`capi-row-${i}`}>
                <td>{e.created_at ? String(e.created_at).slice(0, 19).replace("T", " ") : "—"}</td>
                <td style={{ fontFamily: "monospace" }}>{e.pixel_id}</td>
                <td>{(e.event_names || []).join(", ")} × {e.event_count}</td>
                <td>{e.live ? "live" : "sandbox"}</td>
                <td>{e.status}</td>
                <td>{e.error_code || "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------

export default function AdsInsights() {
  const [tab, setTab] = useState("insights");
  const { adAccounts, adAccountsError } = useAdAccounts();
  const [readiness, setReadiness] = useState(null);
  const [readinessErr, setReadinessErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/ads/live-readiness");
        if (!cancelled) setReadiness(r.data);
      } catch (e) {
        if (!cancelled) setReadinessErr(formatApiError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const appReview = readiness?.app_review || null;
  const missingScopes = [];
  if (appReview) {
    if (appReview.read_insights_granted === false) missingScopes.push("read_insights");
    if (appReview.ads_management_granted === false) missingScopes.push("ads_management");
    if (appReview.ads_read_granted === false) missingScopes.push("ads_read");
  }
  const showBanner = missingScopes.length > 0;

  return (
    <Shell>
      <div className="bo-page" data-testid="ads-growth-page">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Genesis · M8 · Meta Ads growth loop</div>
          <h1
            style={{ fontFamily: "var(--bo-serif)", fontSize: 30, margin: "4px 0" }}
            data-testid="ads-growth-title"
          >
            Ads console
          </h1>
          <p style={{ color: "var(--bo-muted)", margin: 0 }}>
            Conversions API, Custom Audiences, Lead Ads inbox, and Meta
            Insights — all sandboxed until <code>META_ADS_LIVE_ENABLED=1</code>.
          </p>
        </div>

        {showBanner ? (
          <div
            data-testid="app-review-banner"
            style={{
              background: "rgba(179, 74, 74, 0.08)",
              border: "1px solid rgba(179, 74, 74, 0.38)",
              borderRadius: 8,
              padding: "14px 16px",
              marginBottom: 16,
            }}
          >
            <div
              className="bo-eyebrow"
              style={{ color: "#b34a4a", fontSize: 11, letterSpacing: 2 }}
            >
              Meta App Review required
            </div>
            <div
              style={{
                fontFamily: "var(--bo-serif)",
                fontSize: 18,
                margin: "4px 0",
              }}
              data-testid="app-review-banner-title"
            >
              Missing scopes: {missingScopes.join(", ")}
            </div>
            <p style={{ margin: "4px 0 8px", fontSize: 13, color: "var(--bo-muted)" }}>
              {appReview.action_required ||
                "Live metrics are locked at 0 until Meta approves the missing OAuth scopes. Submit an App Review, wait for approval, then reconnect the Page in Channel Connections."}
            </p>
            <ol style={{ margin: "6px 0 0 18px", fontSize: 12, color: "var(--bo-muted)", lineHeight: 1.7 }}>
              <li>Meta App Dashboard → App Review → Permissions and Features → request the missing scope(s).</li>
              <li>Wait for approval (typically 1–3 business days).</li>
              <li>Reconnect the Page in <a href="/brand-os/channels" data-testid="app-review-reconnect-link">Channel Connections</a> so the new OAuth consent grants the scope.</li>
              <li>Refresh this page — the adapter auto-detects the new scope on the next pull.</li>
            </ol>
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 8, fontFamily: "var(--bo-mono, monospace)" }}>
              Checked via {appReview.checked_via}
              {appReview.checked_at ? ` · ${appReview.checked_at.slice(0, 19).replace("T", " ")} UTC` : ""}
            </div>
          </div>
        ) : null}
        {readinessErr && !readiness ? (
          <p style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 8 }} data-testid="app-review-readiness-err">
            App-Review readiness check unavailable ({readinessErr})
          </p>
        ) : null}

        <div
          role="tablist"
          style={{
            display: "flex",
            gap: 4,
            borderBottom: "1px solid var(--bo-border, #e6e2d8)",
            marginBottom: 12,
          }}
        >
          {TAB_KEYS.map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              type="button"
              className={`bo-btn ${tab === k ? "bo-btn--primary" : "bo-btn--ghost"}`}
              onClick={() => setTab(k)}
              data-testid={`ads-tab-${k}`}
              style={{ borderRadius: "6px 6px 0 0" }}
            >
              {TAB_LABELS[k]}
            </button>
          ))}
        </div>

        {adAccountsError ? <ErrorBanner error={adAccountsError} /> : null}

        {tab === "insights" && <InsightsTab ad_accounts={adAccounts} />}
        {tab === "audiences" && <AudiencesTab ad_accounts={adAccounts} />}
        {tab === "leads" && <LeadsTab />}
        {tab === "capi" && <CapiTab ad_accounts={adAccounts} />}
      </div>
    </Shell>
  );
}
